import { distinctIdentityHandle } from "@/src/lib/identityPresentation";
import { Text, TextInput as BrandTextInput } from "@/src/components/ui/BrandText";
import { Ionicons } from "@/src/components/ui/ionicons";
import { db } from "@/src/lib/firebase";
import { getAccountType, getPrimaryIdentityBadge } from "@/src/lib/identity";
import {
  dedupeDiscoveryIdentities,
  isDiscoverableIdentity,
  normalizeDiscoveryText,
  scoreDiscoveryIdentity,
  tokenizeDiscoveryText,
} from "@/src/lib/discovery";
import {
  isSearchRequestCurrent,
  makeSearchRequestKey,
  type SearchRequestIdentity,
} from "@/src/lib/searchRequestIdentity";
import { SUBJECTS, getSubjectLabel, getTrackLabel } from "@/src/lib/taxonomy";
import { useActiveAccount } from "@/src/hooks/useActiveAccount";
import { avatarSourceFor } from "@/src/theme/defaultAvatars";
import { contentMaxWidths, readingContent } from "@/src/theme/layout";
import { border, surface, text as uiText } from "@/src/theme/tokens";
import type {
  AccountType,
  IdentityEntitlements,
  VerificationStatus,
} from "@/src/types/identity";
import { router } from "expo-router";
import {
  collection,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  ActivityIndicator,
  FlatList,
  Image,
  Pressable,
  StyleSheet,
  View,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";

const BG = surface.secondary;
const CARD = surface.primary;
const BORDER = border.subtle;
const MUTED = uiText.muted;
const IDENTITY_SEARCH_LIMIT = 100;
const GROUP_SEARCH_POOL = 50;

const SUBJECT_ALIASES: Record<string, string[]> = {
  arabic: ["arabe"],
  french: ["francais", "français"],
  english: ["anglais"],
  history_geography: ["histoire", "geographie", "géographie", "histoire geo"],
  philosophy: ["philo", "philosophie"],
  mathematics: ["math", "maths", "mathematiques", "mathématiques"],
  physical_sciences: ["physique", "sciences physiques"],
  natural_sciences: ["svt", "sciences naturelles", "biologie"],
  technology: ["technologie"],
  management: ["gestion", "management"],
  law: ["droit"],
  accounting_finance: ["comptabilite", "comptabilité", "finance"],
  islamic_education: ["education islamique", "éducation islamique"],
  third_foreign_language: ["troisieme langue", "troisième langue", "3eme langue"],
};

type Group = {
  id: string;
  name: string;
  description?: string;
  membersCount?: number;
  tags?: string[];
  isPublic?: boolean;
  subject?: string;
};

type IdentityItem = {
  id: string;
  accountId: string;
  accountType: AccountType;
  status: "active" | "disabled";
  discoverable?: boolean;
  username?: string;
  displayName?: string;
  institutionName?: string;
  verificationStatus?: VerificationStatus;
  entitlements?: IdentityEntitlements;
  mainSubject?: string;
  track?: string;
  photoURL?: string;
  badges?: any[];
};

type SubjectResult = {
  value: string;
  label: string;
  aliases: string[];
  identitiesCount: number;
  groupsCount: number;
  score: number;
};

function words(input: string) {
  return normalizeDiscoveryText(input).split(/\s+/).filter(Boolean);
}

function shortHandle(identity: IdentityItem) {
  const raw = String(identity.username ?? "").trim().replace(/^@/, "");
  return raw ? `@${raw}` : null;
}

function roleLabel(identity: IdentityItem) {
  return (getPrimaryIdentityBadge(identity)?.label ?? "Utilisateur").replace(
    "École / Institution",
    "École"
  );
}

function identitySubtitle(identity: IdentityItem) {
  if (getAccountType(identity) === "teacher") {
    return getSubjectLabel(identity.mainSubject || "") || "Professeur";
  }
  if (getAccountType(identity) === "institution") {
    return identity.institutionName || "École / Institution";
  }
  return getTrackLabel(identity.track || "") || "Élève";
}

function maxScore(values: number[]) {
  return values.reduce((best, value) => Math.max(best, value), 0);
}

function scoreField(
  rawValue: string | undefined,
  queryText: string,
  tokens: string[],
  weights: { exact: number; prefix: number; wordPrefix: number; includes: number }
) {
  const value = normalizeDiscoveryText(rawValue ?? "");
  if (!value) return 0;
  if (value === queryText) return weights.exact;
  if (value.startsWith(queryText)) return weights.prefix;
  if (words(value).some((part) => tokens.some((token) => part.startsWith(token)))) {
    return weights.wordPrefix;
  }
  if (tokens.every((token) => value.includes(token))) return weights.includes;
  return 0;
}

function scoreGroup(group: Group, queryText: string, tokens: string[]) {
  const subject = getSubjectLabel(group.subject || "");
  const haystack = normalizeDiscoveryText(
    [group.name, group.description, subject, group.subject, ...(group.tags ?? [])]
      .filter(Boolean)
      .join(" ")
  );
  if (!tokens.every((token) => haystack.includes(token))) return 0;

  return (
    maxScore([
      scoreField(group.name, queryText, tokens, {
        exact: 360,
        prefix: 280,
        wordPrefix: 180,
        includes: 130,
      }),
      scoreField(group.description, queryText, tokens, {
        exact: 120,
        prefix: 90,
        wordPrefix: 70,
        includes: 50,
      }),
      scoreField(subject, queryText, tokens, {
        exact: 130,
        prefix: 100,
        wordPrefix: 70,
        includes: 60,
      }),
    ]) + Math.min(group.membersCount ?? 0, 99)
  );
}

function IdentityCard({ item }: { item: IdentityItem }) {
  const displayName =
    item.displayName?.trim() ||
    item.institutionName?.trim() ||
    shortHandle(item) ||
    "Utilisateur";
  const handle = distinctIdentityHandle(displayName, shortHandle(item));

  return (
    <Pressable
      onPress={() => router.push(`/profile/${item.id}`)}
      accessibilityRole="button"
      accessibilityLabel={`Ouvrir le profil de ${displayName}`}
      style={({ pressed }) => [styles.card, pressed && { opacity: 0.78 }]}
    >
      <Image
        source={avatarSourceFor(item.photoURL, item.id)}
        style={styles.avatar}
        resizeMode="cover"
      />
      <View style={{ flex: 1, minWidth: 0 }}>
        <Text style={styles.cardTitle}>{displayName}</Text>
        <View style={styles.row}>
          {handle ? <Text style={styles.cardDesc}>@{handle}</Text> : null}
          <View style={styles.pill}>
            <Text style={styles.pillText}>{roleLabel(item)}</Text>
          </View>
        </View>
        <Text style={styles.meta}>{identitySubtitle(item)}</Text>
      </View>
      <View style={styles.action}><Text style={styles.actionText}>Voir</Text></View>
    </Pressable>
  );
}

function GroupCard({ item }: { item: Group }) {
  return (
    <Pressable
      onPress={() => router.push(`/group/${item.id}`)}
      accessibilityRole="button"
      accessibilityLabel={`Ouvrir le groupe ${item.name}`}
      style={({ pressed }) => [styles.card, pressed && { opacity: 0.78 }]}
    >
      <View style={{ flex: 1, minWidth: 0 }}>
        <Text style={styles.cardTitle}>{item.name}</Text>
        {item.description ? <Text style={styles.cardDesc}>{item.description}</Text> : null}
        <Text style={styles.meta}>
          {(item.membersCount ?? 0).toLocaleString()} membres{item.subject ? ` · ${getSubjectLabel(item.subject)}` : ""}
        </Text>
      </View>
      <View style={styles.action}><Text style={styles.actionText}>Voir</Text></View>
    </Pressable>
  );
}

export default function SearchScreen() {
  const { activeAccountId } = useActiveAccount();
  const [text, setText] = useState("");
  const [debounced, setDebounced] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingSuggestions, setLoadingSuggestions] = useState(true);
  const [identities, setIdentities] = useState<IdentityItem[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [subjects, setSubjects] = useState<SubjectResult[]>([]);
  const [recommendedTeachers, setRecommendedTeachers] = useState<IdentityItem[]>([]);
  const [recommendedGroups, setRecommendedGroups] = useState<Group[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [retryKey, setRetryKey] = useState(0);
  const searchGenerationRef = useRef(0);
  const currentSearchKeyRef = useRef("");
  const searchIdentityRef = useRef<SearchRequestIdentity>({ generation: 0, key: "" });
  currentSearchKeyRef.current = makeSearchRequestKey(
    normalizeDiscoveryText(text.trim()),
    activeAccountId,
    retryKey
  );
  searchIdentityRef.current = {
    generation: searchGenerationRef.current,
    key: currentSearchKeyRef.current,
  };

  function changeSearchText(nextText: string) {
    const generation = ++searchGenerationRef.current;
    const key = makeSearchRequestKey(
      normalizeDiscoveryText(nextText.trim()),
      activeAccountId,
      retryKey
    );
    currentSearchKeyRef.current = key;
    searchIdentityRef.current = { generation, key };
    setText(nextText);
  }

  function retrySearch() {
    const nextRetryKey = retryKey + 1;
    const generation = ++searchGenerationRef.current;
    const key = makeSearchRequestKey(
      normalizeDiscoveryText(text.trim()),
      activeAccountId,
      nextRetryKey
    );
    currentSearchKeyRef.current = key;
    searchIdentityRef.current = { generation, key };
    setRetryKey(nextRetryKey);
  }

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(text.trim()), 250);
    return () => clearTimeout(timer);
  }, [text]);

  useEffect(() => {
    setLoadingSuggestions(true);
    let remaining = 2;
    const settle = () => {
      remaining -= 1;
      if (remaining <= 0) setLoadingSuggestions(false);
    };

    const teacherQuery = query(
      collection(db, "accounts"),
      where("status", "==", "active"),
      where("accountType", "==", "teacher"),
      limit(12)
    );
    const groupQuery = query(collection(db, "groups"), orderBy("membersCount", "desc"), limit(10));

    const unsubscribeTeachers = onSnapshot(
      teacherQuery,
      (snapshot) => {
        setRecommendedTeachers(
          snapshot.docs
            .map((doc) => ({ ...(doc.data() as any), id: doc.id, accountId: doc.id } as IdentityItem))
            .filter((item) => item.id !== activeAccountId)
            .slice(0, 8)
        );
        settle();
      },
      () => {
        setError("Impossible de charger les professeurs recommandés.");
        settle();
      }
    );

    const unsubscribeGroups = onSnapshot(
      groupQuery,
      (snapshot) => {
        setRecommendedGroups(snapshot.docs.map((doc) => ({ id: doc.id, ...(doc.data() as any) })));
        settle();
      },
      () => {
        setError("Impossible de charger les groupes recommandés.");
        settle();
      }
    );

    return () => {
      unsubscribeTeachers();
      unsubscribeGroups();
    };
  }, [activeAccountId, retryKey]);

  useEffect(() => {
    let cancelled = false;

    async function run() {
      const clean = debounced.trim();
      const normalized = normalizeDiscoveryText(clean);
      const key = makeSearchRequestKey(normalized, activeAccountId, retryKey);
      const identity: SearchRequestIdentity = {
        generation: ++searchGenerationRef.current,
        key,
      };

      if (key !== currentSearchKeyRef.current) {
        searchIdentityRef.current = {
          generation: identity.generation,
          key: currentSearchKeyRef.current,
        };
        return;
      }
      searchIdentityRef.current = identity;
      const isCurrent = () => !cancelled && isSearchRequestCurrent(identity, searchIdentityRef.current);

      if (normalized.length < 2) {
        if (isCurrent()) {
          setLoading(false);
          setIdentities([]);
          setGroups([]);
          setSubjects([]);
          setError(null);
        }
        return;
      }

      setLoading(true);
      setError(null);
      const tokens = tokenizeDiscoveryText(clean);
      const primaryToken = tokens[0];

      try {
        const identityPromise = getDocs(
          query(
            collection(db, "accounts"),
            where("status", "==", "active"),
            where("searchPrefixes", "array-contains", primaryToken),
            limit(IDENTITY_SEARCH_LIMIT)
          )
        );
        const groupPromise = getDocs(
          query(collection(db, "groups"), orderBy("membersCount", "desc"), limit(GROUP_SEARCH_POOL))
        );
        const tagPromise = tokens.length
          ? getDocs(
              query(
                collection(db, "groups"),
                where("tags", "array-contains-any", tokens.slice(0, 10)),
                limit(GROUP_SEARCH_POOL)
              )
            )
          : Promise.resolve(null);

        const [identitySnapshot, groupSnapshot, tagSnapshot] = await Promise.all([
          identityPromise,
          groupPromise,
          tagPromise,
        ]);

        const rankedIdentities = dedupeDiscoveryIdentities(identitySnapshot.docs
          .map((doc) => ({ ...(doc.data() as any), id: doc.id, accountId: doc.id } as IdentityItem)))
          .filter((item) => item.id !== activeAccountId && isDiscoverableIdentity(item))
          .map((item) => ({
            item,
            score: scoreDiscoveryIdentity(item, normalized, {
              subject: getSubjectLabel(item.mainSubject || ""),
              track: getTrackLabel(item.track || ""),
            }) + (item.photoURL ? 4 : 0),
          }))
          .filter((entry) => entry.score > 0)
          .sort((a, b) => b.score - a.score)
          .map((entry) => entry.item)
          .slice(0, 20);

        const groupCandidates = [
          ...groupSnapshot.docs.map((doc) => ({ id: doc.id, ...(doc.data() as any) } as Group)),
          ...(tagSnapshot?.docs.map((doc) => ({ id: doc.id, ...(doc.data() as any) } as Group)) ?? []),
        ].filter((group, index, list) => list.findIndex((candidate) => candidate.id === group.id) === index);
        const rankedGroups = groupCandidates
          .map((item) => ({ item, score: scoreGroup(item, normalized, tokens) }))
          .filter((entry) => entry.score > 0)
          .sort((a, b) => b.score - a.score)
          .map((entry) => entry.item)
          .slice(0, 20);

        const rankedSubjects = SUBJECTS.map((subject) => {
          const aliases = SUBJECT_ALIASES[subject.value] ?? [];
          const haystack = normalizeDiscoveryText([subject.value, subject.label, ...aliases].join(" "));
          const matched = tokens.every((token) => haystack.includes(token));
          const identitiesCount = rankedIdentities.filter(
            (item) => normalizeDiscoveryText(item.mainSubject || "") === normalizeDiscoveryText(subject.value)
          ).length;
          const groupsCount = rankedGroups.filter(
            (item) => normalizeDiscoveryText(item.subject || "") === normalizeDiscoveryText(subject.value)
          ).length;
          return {
            value: subject.value,
            label: subject.label,
            aliases,
            identitiesCount,
            groupsCount,
            score: (matched ? 100 : 0) + identitiesCount * 12 + groupsCount * 10,
          };
        })
          .filter((subject) => subject.score > 0)
          .sort((a, b) => b.score - a.score)
          .slice(0, 8);

        if (isCurrent()) {
          setIdentities(rankedIdentities);
          setGroups(rankedGroups);
          setSubjects(rankedSubjects);
        }
      } catch {
        if (isCurrent()) {
          setError("Impossible d'effectuer la recherche.");
          setIdentities([]);
          setGroups([]);
          setSubjects([]);
        }
      } finally {
        if (isCurrent()) setLoading(false);
      }
    }

    run();
    return () => {
      cancelled = true;
    };
  }, [activeAccountId, debounced, retryKey]);

  const showResults = text.trim().length > 0;
  const queryPending = showResults && (text.trim() !== debounced.trim() || loading);
  const queryTooShort = showResults
    && text.trim() === debounced.trim()
    && normalizeDiscoveryText(text.trim()).length < 2;
  const showError = showResults && !queryPending && !queryTooShort && !!error;
  const resultsCount = useMemo(
    () => identities.length + groups.length + subjects.length,
    [groups.length, identities.length, subjects.length]
  );

  return (
    <SafeAreaView style={styles.container} edges={["top", "left", "right"]}>
      <View style={styles.header}>
        <Text style={styles.title}>Recherche</Text>
        <View style={styles.searchWrap}>
          <Ionicons name="search" size={20} color={MUTED} />
          <BrandTextInput
            value={text}
            onChangeText={changeSearchText}
            placeholder="Personne, école, groupe ou matière..."
            placeholderTextColor={uiText.muted}
            style={styles.searchInput}
            autoCapitalize="none"
            autoCorrect={false}
            accessibilityLabel="Rechercher une personne, une école, un groupe ou une matière"
          />
          {queryPending ? <ActivityIndicator color={uiText.brand} /> : null}
          {!queryPending && text ? (
            <Pressable
              onPress={() => changeSearchText("")}
              accessibilityRole="button"
              accessibilityLabel="Effacer la recherche"
              hitSlop={10}
            >
              <Ionicons name="close-circle" size={22} color={MUTED} />
            </Pressable>
          ) : null}
        </View>
        {showResults ? (
          <Text style={styles.subtitle}>
            {queryPending
              ? `Recherche de ${text.trim()}…`
              : queryTooShort
                ? "Saisis au moins 2 caractères pour chercher."
                : `${resultsCount} résultat${resultsCount > 1 ? "s" : ""} pour ${text.trim()}`}
          </Text>
        ) : null}
        {showError ? (
          <Pressable style={styles.error} onPress={retrySearch}>
            <Text style={styles.errorText}>{error} Réessayer</Text>
          </Pressable>
        ) : null}
      </View>

      <FlatList
        style={{ flex: 1, width: "100%", minHeight: 0 }}
        data={showResults
          ? queryPending
            ? ["searching"]
            : queryTooShort
              ? []
              : resultsCount > 0 ? ["identities", "groups", "subjects"] : []
          : ["teachers", "recommendedGroups"]}
        keyExtractor={(item) => item}
        contentContainerStyle={{ ...readingContent(contentMaxWidths.search), paddingHorizontal: 16, paddingBottom: 24 }}
        renderItem={({ item }) => {
          if (item === "searching") {
            return <View style={styles.loading}><ActivityIndicator color={uiText.brand} /></View>;
          }
          if (item === "identities") {
            if (!identities.length) return null;
            return <View><Text style={styles.sectionTitle}>Identités</Text>{identities.map((identity) => <IdentityCard key={identity.id} item={identity} />)}</View>;
          }
          if (item === "groups") {
            if (!groups.length) return null;
            return <View><Text style={styles.sectionTitle}>Groupes</Text>{groups.map((group) => <GroupCard key={group.id} item={group} />)}</View>;
          }
          if (item === "subjects") {
            if (!subjects.length) return null;
            return (
              <View>
                <Text style={styles.sectionTitle}>Matières</Text>
                {subjects.map((subject) => (
                  <Pressable key={subject.value} style={styles.card} onPress={() => changeSearchText(subject.label)}>
                    <View style={{ flex: 1 }}>
                      <Text style={styles.cardTitle}>{subject.label}</Text>
                      <Text style={styles.meta}>{subject.identitiesCount} identité{subject.identitiesCount > 1 ? "s" : ""} · {subject.groupsCount} groupe{subject.groupsCount > 1 ? "s" : ""}</Text>
                    </View>
                    <View style={styles.action}><Text style={styles.actionText}>Explorer</Text></View>
                  </Pressable>
                ))}
              </View>
            );
          }
          if (item === "teachers") {
            if (loadingSuggestions) return <View style={styles.loading}><ActivityIndicator color={uiText.brand} /></View>;
            if (!recommendedTeachers.length) return null;
            return <View><Text style={styles.sectionTitle}>Professeurs recommandés</Text>{recommendedTeachers.map((identity) => <IdentityCard key={identity.id} item={identity} />)}</View>;
          }
          if (!recommendedGroups.length) return null;
          return <View><Text style={styles.sectionTitle}>Groupes recommandés</Text>{recommendedGroups.map((group) => <GroupCard key={group.id} item={group} />)}</View>;
        }}
        ListEmptyComponent={
          queryPending || queryTooShort || showError ? null : (
            <View style={styles.empty}>
              <Text style={styles.cardTitle}>Aucun résultat</Text>
              <Text style={styles.cardDesc}>Essaie un autre nom, école, groupe ou matière.</Text>
            </View>
          )
        }
      />
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: BG },
  header: { paddingHorizontal: 16, alignSelf: "center", maxWidth: contentMaxWidths.search, paddingBottom: 12, paddingTop: 8, width: "100%" },
  title: { color: uiText.primary, fontSize: 28, fontWeight: "800" },
  subtitle: { color: MUTED, marginTop: 8 },
  searchWrap: { marginTop: 12, backgroundColor: CARD, borderRadius: 16, borderWidth: 1, borderColor: BORDER, paddingHorizontal: 12, height: 48, alignItems: "center", flexDirection: "row", gap: 10 },
  searchInput: { flex: 1, color: uiText.primary, fontSize: 15 },
  sectionTitle: { color: uiText.primary, fontSize: 16, fontWeight: "800", marginBottom: 10, marginTop: 10 },
  card: { backgroundColor: CARD, borderRadius: 16, borderWidth: 1, borderColor: BORDER, padding: 12, flexDirection: "row", alignItems: "center", gap: 12, marginBottom: 8 },
  cardTitle: { color: uiText.primary, fontSize: 15, fontWeight: "800" },
  cardDesc: { color: MUTED, marginTop: 4 },
  row: { alignItems: "center", flexDirection: "row", flexWrap: "wrap", gap: 8, marginTop: 4 },
  meta: { color: uiText.muted, fontSize: 12, marginTop: 6 },
  pill: { paddingHorizontal: 8, height: 22, borderRadius: 999, borderWidth: 1, borderColor: border.brand, backgroundColor: surface.selected, alignItems: "center", justifyContent: "center" },
  pillText: { color: uiText.primary, fontSize: 10, fontWeight: "900" },
  action: { backgroundColor: surface.selected, borderColor: border.brand, borderWidth: 1, paddingHorizontal: 12, height: 34, borderRadius: 999, alignItems: "center", justifyContent: "center" },
  actionText: { color: uiText.primary, fontWeight: "900", fontSize: 12 },
  avatar: { width: 44, height: 44, borderRadius: 999, borderWidth: 1, borderColor: border.brand },
  empty: { marginTop: 12, backgroundColor: CARD, borderColor: BORDER, borderWidth: 1, borderRadius: 16, padding: 16 },
  loading: { padding: 24, alignItems: "center" },
  error: { marginTop: 10, padding: 12, borderRadius: 14, borderWidth: 1, borderColor: border.danger, backgroundColor: surface.danger },
  errorText: { color: uiText.danger, fontWeight: "800" },
});
